import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import SafeIcon from '../../../common/SafeIcon';
import InternalChat from '../../../components/InternalChat';
import supabase from '../../../supabase/supabase';

const { FiClock, FiCheckCircle, FiAlertCircle, FiMessageSquare, FiFileText, FiArrowLeft, FiInfo, FiMail, FiBell, FiBellOff, FiLoader, FiCheck } = FiIcons;

const WhistleblowerDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reports, updateReport } = useData();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evidenceRequests, setEvidenceRequests] = useState([]);
  
  const [isUpdatingPrefs, setIsUpdatingPrefs] = useState(false);
  const [showPrefsSaved, setShowPrefsSaved] = useState(false);
  const [prefs, setPrefs] = useState({
    enabled: false,
    email: ''
  });

  useEffect(() => {
    const foundReport = reports.find(r => r.id === id);
    if (foundReport) {
      setReport(foundReport);
      setPrefs({
        enabled: foundReport.notifications_enabled || false,
        email: foundReport.notification_email || ''
      });
      fetchEvidenceRequests(foundReport.id);
    }
    setLoading(false);
  }, [id, reports]);

  const fetchEvidenceRequests = async (reportId) => {
    try {
      const { data, error } = await supabase
        .from('evidence_requests')
        .select('*')
        .eq('report_id', reportId)
        .eq('target_role', 'whistleblower')
        .eq('status', 'pending');
      
      if (!error) {
        setEvidenceRequests(data || []);
      }
    } catch (err) {
      console.error("Error fetching evidence requests:", err);
    }
  };

  const handleUpdatePrefs = async () => {
    setIsUpdatingPrefs(true);
    try {
      const { error } = await supabase
        .from('reports')
        .update({
          notifications_enabled: prefs.enabled,
          notification_email: prefs.email
        })
        .eq('id', report.id);

      if (!error) {
        setShowPrefsSaved(true);
        setTimeout(() => setShowPrefsSaved(false), 3000);
      }
    } catch (err) {
      console.error("Error updating preferences:", err);
    } finally {
      setIsUpdatingPrefs(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Laster status...</div>;
  if (!report) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-2xl max-w-md text-center">
        <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Fant ikke rapporten</h2>
        <p className="text-slate-400 mb-6">Referansekoden er ugyldig eller saken er slettet.</p>
        <button 
          onClick={() => navigate('/varsle/logg-inn')}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-colors"
        >
          Gå til logg inn
        </button>
      </div>
    </div>
  );

  const steps = [
    { id: 'new', label: 'Mottatt', description: report.is_confirmed ? 'Mottak er bekreftet av saksbehandler.' : 'Rapporten er mottatt og venter på bekreftelse.' },
    { id: 'investigating', label: 'Under vurdering', description: 'Saksbehandler undersøker saken.' },
    { id: 'resolved', label: 'Behandlet', description: 'Saken er ferdigstilt og konklusjon er trukket.' }
  ];

  const currentStepIndex = steps.findIndex(s => s.id === report.status);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 pb-20">
      <div className="max-w-5xl mx-auto px-4 pt-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Tilbake</span>
            </button>
            <h1 className="text-3xl font-bold text-white">Saksstatus</h1>
            <p className="text-slate-400 mt-1">Saksnummer: <span className="text-indigo-400 font-mono">{report.id}</span></p>
          </div>
          
          <div className="flex items-center gap-3 bg-slate-900/50 border border-slate-800 p-4 rounded-2xl">
            <div className={`w-3 h-3 rounded-full animate-pulse ${
              report.status === 'new' ? 'bg-blue-400' : 
              report.status === 'investigating' ? 'bg-amber-400' : 'bg-green-400'
            }`} />
            <span className="font-medium">
              {report.status === 'new' ? 'Ny / Mottatt' : 
               report.status === 'investigating' ? 'Under behandling' : 'Saken er avsluttet'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Status Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Active Requests Notice */}
            {evidenceRequests.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-3xl flex items-start gap-4">
                <div className="p-3 bg-amber-500/20 text-amber-500 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-500">Oppfølging kreves</h3>
                  <p className="text-sm text-amber-200/70 mt-1">
                    Saksbehandler har etterspurt mer informasjon eller bevis. Vennligst se dialogfeltet nedenfor for detaljer.
                  </p>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-3xl">
              <h3 className="text-lg font-semibold text-white mb-8 flex items-center gap-2">
                <SafeIcon icon={FiClock} className="text-indigo-400" />
                Fremdriftsplan
              </h3>
              
              <div className="relative">
                {steps.map((step, index) => {
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  
                  return (
                    <div key={step.id} className="flex gap-6 mb-8 last:mb-0 relative">
                      {index !== steps.length - 1 && (
                        <div className={`absolute left-4 top-10 bottom-[-20px] w-0.5 ${
                          index < currentStepIndex ? 'bg-indigo-500' : 'bg-slate-800'
                        }`} />
                      )}
                      
                      <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        isCompleted ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' : 'bg-slate-800 text-slate-500 border border-slate-700'
                      }`}>
                        {isCompleted ? <SafeIcon icon={FiCheckCircle} /> : <span>{index + 1}</span>}
                      </div>
                      
                      <div>
                        <h4 className={`font-bold ${isCompleted ? 'text-white' : 'text-slate-500'}`}>
                          {step.label}
                        </h4>
                        <p className="text-sm text-slate-400 mt-1">{step.description}</p>
                        {isCurrent && (
                          <span className="inline-block mt-2 px-3 py-1 bg-indigo-500/10 text-indigo-400 text-xs font-semibold rounded-full border border-indigo-500/20">
                            Nåværende status
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Case Outcome Summary */}
              {report.status === 'resolved' && (report.outcome_summary || report.remediation_action) && (
                <div className="mt-10 pt-8 border-t border-slate-800 space-y-6">
                  <h3 className="text-lg font-semibold text-green-400 flex items-center gap-2">
                    <SafeIcon icon={FiCheckCircle} />
                    Konklusjon og tiltak
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {report.outcome_summary && (
                      <div className="p-5 bg-slate-800/30 rounded-2xl border border-slate-700/50">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Oppsummering</span>
                        <p className="text-sm text-slate-300 leading-relaxed italic">"{report.outcome_summary}"</p>
                      </div>
                    )}
                    {report.remediation_action && (
                      <div className="p-5 bg-slate-800/30 rounded-2xl border border-slate-700/50">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Iverksatte tiltak</span>
                        <p className="text-sm text-slate-300 leading-relaxed italic">"{report.remediation_action}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Communication */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl overflow-hidden h-[500px] flex flex-col">
              <div className="p-6 border-b border-slate-800 bg-slate-900/80">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <SafeIcon icon={FiMessageSquare} className="text-indigo-400" />
                    Anonym Dialog
                  </h3>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-[10px] font-bold text-green-400 uppercase tracking-wider">Kryptert</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">Du kan trygt svare på spørsmål her uten å avsløre identiteten din.</p>
              </div>
              <div className="flex-1 overflow-hidden">
                <InternalChat 
                  reportId={report.id} 
                  currentUserRole="whistleblower"
                />
              </div>
            </div>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            {/* Notifications Section */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Statusvarsling</h3>
                <SafeIcon icon={prefs.enabled ? FiBell : FiBellOff} className={prefs.enabled ? "text-green-400" : "text-slate-600"} />
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <span className="text-xs text-slate-300">Aktiver e-postvarsler</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={prefs.enabled} 
                      onChange={e => setPrefs(prev => ({ ...prev, enabled: e.target.checked }))} 
                      className="sr-only peer" 
                    />
                    <div className="w-10 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                {prefs.enabled && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                    <input 
                      type="email" 
                      value={prefs.email}
                      onChange={e => setPrefs(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="varsling@epost.no"
                      className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs outline-none focus:border-indigo-500 transition-all"
                    />
                    <p className="text-[10px] text-slate-500 italic">
                      Din e-post brukes kun til systemvarsler og er skjult for saksbehandler.
                    </p>
                  </div>
                )}

                <button 
                  onClick={handleUpdatePrefs}
                  disabled={isUpdatingPrefs}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {isUpdatingPrefs ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />}
                  <span>Lagre innstillinger</span>
                </button>

                <AnimatePresence>
                  {showPrefsSaved && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-center text-[10px] text-green-400 font-bold"
                    >
                      Innstillinger oppdatert!
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Deadlines */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Viktige Frister</h3>
              <div className="space-y-4">
                <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm text-slate-300">Bekreftelse</span>
                    {report.is_confirmed ? (
                      <span className="text-xs text-green-400 font-medium">Fullført</span>
                    ) : (
                      <span className="text-xs text-amber-400 font-medium">Innen 7 dager</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Du skal ha bekreftelse på mottak innen 7 dager etter innsending.</p>
                </div>

                <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm text-slate-300">Tilbakemelding</span>
                    <span className="text-xs text-indigo-400 font-medium">Innen 3 mnd</span>
                  </div>
                  <p className="text-xs text-slate-500">Endelig tilbakemelding om tiltak skal gis innen 3 måneder.</p>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-indigo-500/5 border border-indigo-500/20 p-6 rounded-3xl">
              <div className="flex gap-3">
                <SafeIcon icon={FiInfo} className="text-indigo-400 shrink-0 mt-1" />
                <div>
                  <h4 className="text-sm font-bold text-indigo-300">Sikkerhet</h4>
                  <p className="text-xs text-indigo-300/70 mt-1 leading-relaxed">
                    Husk å ta vare på referansekoden din. Dette er den eneste måten du kan få tilgang til saken på. Vi lagrer ingen personopplysninger om din nettleser eller IP-adresse.
                  </p>
                </div>
              </div>
            </div>

            {/* Evidence Archive */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Dine Vedlegg</h3>
              {report.evidence_urls && report.evidence_urls.length > 0 ? (
                <div className="space-y-2">
                  {report.evidence_urls.map((url, i) => (
                    <a 
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 hover:border-indigo-500/50 transition-colors group"
                    >
                      <SafeIcon icon={FiFileText} className="text-slate-400 group-hover:text-indigo-400" />
                      <span className="text-xs truncate">Vedlegg {i + 1}</span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">Ingen vedlegg lastet opp.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhistleblowerDashboard;